/**
 * DES RÉPONSES DU LECTEUR AUX GRAPHES DE L'ONGLET (DEV-1508 lot 2, E3 et E7).
 * ============================================================================
 *
 * INTENTION : un onglet reçoit des séries colonnaires ; l'écran doit montrer des
 * courbes lisibles, chacune avec son unité, son origine et ce qui lui manque.
 * Ce module fait cette traduction, et rien d'autre. Il ne trace pas, il ne charge
 * pas : il range.
 *
 * Quatre règles, toutes tenues ici plutôt que dans un template.
 *
 * 1. L'UNITÉ VIENT DE `meta.unites`, jamais du nom de la variable. Le même
 *    `puissanceactive_*_tout.max` vaut des kVA (puissance maximale mesurée) ou
 *    des kW (enveloppe dérivée du pas fin) : deux grandeurs sous un nom.
 *
 * 2. UN GRAPHE = UNE UNITÉ ET UN SENS. Le regroupement se fait par unité, donc
 *    des kVA et des kW ne peuvent PAS se retrouver sur le même axe. C'est la
 *    seule protection qui tienne : une échelle partagée entre deux grandeurs
 *    fait lire un dépassement là où il n'y en a pas.
 *
 * 3. L'ORIGINE SE LIT DES POINTS, elle ne se suppose pas (décision du 11/09) :
 *    chaque point porte sa provenance, et `utils/origineEnedis.ts` en tire les
 *    quatre mots et leurs parts. Un drapeau posé sur la série entière ne suffit
 *    PLUS JAMAIS seul : la moyenne horaire d'un compteur réel arrive calculée
 *    point par point sans qu'aucun drapeau de série ne le dise, et s'affichait
 *    donc « Mesuré ».
 *
 * 4. UNE SÉRIE NON PUBLIÉE SE DIT EN MENTION, sur la série, là où elle manque.
 *    Elle n'est pas tracée comme une ligne de zéros et n'allume aucune bannière
 *    (décision de Mathieu du 08/09).
 *
 * 5. UNE STATISTIQUE OMISE SOUS PLAFOND SE DIT DE LA MÊME FAÇON. Au-delà de la
 *    fenêtre où le calcul se fait, le lecteur n'invente pas une enveloppe vide :
 *    il l'OMET et donne sa raison dans `meta.plafond_applique`. La page écrit
 *    alors une mention à l'endroit où la courbe aurait été, avec la fenêtre du
 *    calcul. Sans elle, l'écran serait exactement celui d'un compteur qui ne
 *    publie rien, ce qui est faux.
 */
import type {
    DirectionEnedis,
    GrainSeries,
    NatureSeries,
    PlafondApplique,
    PointX,
    ProvenanceSeries,
    ReponseSeries,
    StatSeries,
} from "@modules/data/services/sourceDataEnedis.service"
import {
    LIBELLES_DIRECTION,
    type OngletResolu,
    type OrigineSerie,
    type SerieOnglet,
} from "@modules/data/utils/ongletsEnedis"
import {
    attenduParPoint,
    origineDesPoints,
    origineDominante,
    type RepartitionOrigine,
} from "@modules/data/utils/origineEnedis"

/** Une série prête à peindre, avec tout ce qu'il faut dire à côté d'elle. */
export type SerieTracee = {
    cle: string
    libelle: string
    /**
     * LE LIBELLÉ DE LA GRANDEUR, SANS SA STATISTIQUE (lot 5d).
     *
     * `libelle` porte « Puissance active (moyenne) » dès que plusieurs
     * statistiques sont servies pour une même grandeur. Le titre du graphe et le
     * nom de l'enveloppe ont besoin de la grandeur SEULE : « Puissance active ».
     * La dériver en coupant la parenthèse serait une devinette sur du texte ; on
     * la garde telle qu'elle a été composée.
     */
    libelleBase: string
    /**
     * LA FAMILLE, SANS SA CLASSE TEMPORELLE (lot 5d3).
     *
     * Un C2 sert cinq durées de dépassement, une par classe. Le titre de leur
     * carte nomme la famille une fois — « Durée de dépassement (h) par classe
     * temporelle » — et laisse les classes à la légende. `libelleBase` ne
     * pourrait pas le dire : il porte déjà la classe.
     */
    libelleFamille: string
    variable: string
    stat: StatSeries
    direction: DirectionEnedis
    /** Lue de `meta.unites`, ou déclarée par la carte des onglets. */
    unite: string | null
    /** Nature servie quand le nom ne suffit pas à dire la grandeur. */
    nature: NatureSeries | null
    /**
     * L'origine de la série : celle de ses points quand ils s'accordent, sinon
     * la plus représentée. Les parts vivent dans `origines`.
     */
    origine: OrigineSerie
    /** L'origine LUE point par point, avec ses parts (décision du 11/09). */
    origines: RepartitionOrigine
    /**
     * Ce qu'il faut dire de la GRANDEUR elle-même, quand son nom pourrait la
     * faire prendre pour une autre. `null` quand il n'y a rien à ajouter.
     */
    mention: string | null
    x: PointX[]
    /** `null` = pas non collecté. Jamais 0. */
    v: Array<number | null>
    t: Array<ProvenanceSeries | null>
    /**
     * Points RÉELLEMENT agrégés derrière chaque valeur, aligné sur `x`, quand le
     * lecteur les compte. `null` = il ne l'a pas dit : la couverture reste
     * inconnue, elle ne se déduit pas.
     */
    n: Array<number | null> | null
    /** Vrai là où le lecteur dit la valeur agrégée sur moins que l'attendu. */
    partiel: Array<boolean | null> | null
    /**
     * Ce qu'un point DEVAIT agréger, quand on peut le savoir (le pas du compteur
     * dans une heure). `null` ailleurs : un attendu plausible ferait passer des
     * calculs complets pour des estimations.
     */
    attendu: number | null
    /**
     * L'HEURE D'ATTEINTE, alignée sur `x`, quand le lecteur la sert (`meta.at`).
     *
     * Elle n'existe que pour une PMax RELEVÉE par le compteur : le distributeur
     * publie la valeur ET le moment où elle a été atteinte. `null` = le lecteur
     * ne l'a pas donnée, et elle ne se déduit d'aucun autre point.
     */
    at: Array<string | null> | null
    /**
     * LA NATURE DE CHAQUE PÉRIODE DE FACTURATION (`REEL`, `ESTIME`, `REGU`),
     * alignée sur `x`, telle que `meta.nature` la sert.
     *
     * `null` sur un point veut dire que le lecteur ne l'a pas dite : ce n'est
     * PAS « réel ». Un relevé estimé se peint atténué, un relevé de nature
     * inconnue se peint plein et se lit dans le survol comme non servie.
     */
    natureParPoint: Array<string | null> | null
    /**
     * Vrai quand la série EST la puissance maximale relevée par le compteur
     * (nature `pmax_apparente_r66`), et non un maximum dérivé de la courbe.
     *
     * C'est ce fait — et lui seul — qui sort la série de la carte de la courbe :
     * des kVA relevés et des kW dérivés ne partagent ni axe ni figure.
     */
    pmaxMesuree: boolean
    /**
     * Le compteur ne publie pas cette grandeur. On l'écrit en mention, on ne
     * trace pas une ligne plate qui se lirait « zéro ».
     */
    nonPubliee: boolean
    /**
     * Combien d'abscisses portaient DEUX valeurs finies lors du recollage de
     * deux codes de la même classe (lot 5d4). `undefined` = aucun recollage.
     * La première valeur a été gardée ; rien n'a jamais été additionné.
     */
    conflitsDeFusion?: number
    /**
     * `variables_datav2` DÉCLARE-T-IL cette variable AU GRAIN AFFICHÉ (5d4) ?
     *
     * FAIT VÉRIFIÉ en prod le 16/09 sur le C4 `638f1080`, réactive totale au
     * grain horaire du 01 au 03/07 : la moyenne revient avec 48 points finis
     * sur 72 — dapi la dérive du natif au vol — tandis que le minimum et le
     * maximum reviennent en 72 abscisses TOUTES nulles. Depuis `/series`, cette
     * forme est indistinguable d'une absence réelle sur la fenêtre.
     *
     * Le seul fait qui les sépare est la déclaration : ce compteur ne déclare
     * la réactive qu'au grain `natif`. Une statistique que dapi ne sert pas à
     * ce grain n'est donc pas « absente de la fenêtre » : elle ne se liste pas.
     *
     * `undefined` = la carte n'a pas dit : on ne filtre rien plutôt que de
     * faire disparaître une absence vraie.
     */
    grainDeclare?: boolean
}

/** Un graphe : un sens, une unité, les séries qui la partagent. */
export type GrapheEnedis = {
    cle: string
    titre: string
    unite: string | null
    direction: DirectionEnedis
    series: SerieTracee[]
    /** Les statistiques omises sous plafond, à dire là où elles manquent. */
    plafonnees: SeriePlafonnee[]
    /**
     * LA PMAX MESURÉE POSÉE EN POINTS SUR CETTE FIGURE (complément 7.11).
     *
     * Au niveau journalier — et là seulement — la PMax relevée se lit EN PLUS
     * sur la figure de la courbe : les deux racontent la même journée, et
     * l'oeil doit pouvoir les comparer sans changer de carte. Elle garde sa
     * propre carte : cette copie-ci est une superposition, pas un déménagement.
     *
     * Elle ne partage JAMAIS l'axe des ordonnées de la courbe (des kVA relevés
     * contre des kW dérivés) : le tracé lui donne son axe à elle.
     */
    pmaxPosee?: SerieTracee | null
    /**
     * LE MIROIR : soutirage vers le haut, injection vers le bas, sur UNE figure
     * et un seul axe (lot 5g, spec validée par Mathieu, Q13).
     *
     * Ce que le client y gagne : sur un site qui produit et consomme, la
     * question est « quand est-ce que je tire, quand est-ce que je rends », et
     * deux figures empilées à deux échelles ne la laissent pas lire. Le miroir
     * la donne d'un coup d'oeil.
     *
     * CE QUE CE N'EST PAS, et ne sera jamais : une somme, un solde, un net. Les
     * deux jeux de valeurs restent entiers, l'injection est seulement DESSINÉE
     * en négatif, et le survol la lit en valeur absolue.
     */
    miroir?: boolean
}

/**
 * Une statistique DEMANDÉE que le lecteur n'a pas servie parce que la fenêtre
 * dépassait celle de son calcul. Elle n'a pas de points : elle a une phrase.
 */
export type SeriePlafonnee = {
    cle: string
    variable: string
    direction: DirectionEnedis
    /** Les statistiques omises ensemble, dans l'ordre où on les lit. */
    stats: StatSeries[]
    /** Le libellé de la série concernée, tel qu'il s'écrit sur le graphe. */
    libelle: string
    texte: string
}

/** Le mot d'une statistique, tel qu'il s'écrit à côté d'une série. */
export const LIBELLES_STAT: Record<StatSeries, string> = {
    mean: "moyenne",
    min: "minimum",
    max: "maximum",
    sum: "total",
    last: "dernier relevé",
}

/** La mention servie à côté d'une série que le compteur ne publie pas. */
export const MENTION_NON_PUBLIEE = "Non publié"

// ============================================================================
// LA PMAX RELEVÉE PAR LE COMPTEUR (DEV-1508 lot 5d2, écart G6, complément 7.11)
// ============================================================================
//
// CE QUI EST SERVI, LU SUR LES TÉMOINS RÉELS LE 15/09 (dapi prod, lecture seule) :
//
//  • C5 `6931c727`, grain journalier : `puissanceactive_elecsoutirage_tout.max`
//    arrive en **kVA**, `meta.natures` dit `pmax_apparente_r66`, et `meta.at`
//    donne l'heure d'atteinte de chaque jour (« 18:08:13 »). La moyenne du même
//    appel arrive en **kW**, dérivée de l'horaire ;
//  • C2 `6a8eebfa`, même appel, même grain : le maximum arrive en **kW**, nature
//    `max_moyennes_10min_active`, calculé depuis le natif. Aucune heure.
//
// DEUX GRANDEURS SOUS UN NOM. La PMax relevée est une puissance APPARENTE que le
// compteur a mesurée ; le maximum dérivé est le plus haut des moyennes calculées
// sur le pas. Les poser sur le même axe ferait lire un dépassement là où il n'y
// en a pas. La nature — et elle seule, jamais l'unité, jamais le grain — décide.

/** La nature que le lecteur sert pour une PMax RELEVÉE par le compteur. */
export const NATURE_PMAX_MESUREE = "pmax_apparente_r66"

/** Vrai quand la série est la PMax relevée, et non un maximum dérivé. */
export function estPmaxMesuree(
    nature: NatureSeries | null | undefined
): boolean {
    return nature === NATURE_PMAX_MESUREE
}

/**
 * Le nom d'une PMax relevée, au grain servi. Le grain N'EST PAS cosmétique :
 * une PMax quotidienne et une PMax mensuelle ne répondent pas à la même
 * question, et le distributeur ne les relève pas au même rythme.
 */
export function libellePmaxMesuree(
    grain: GrainSeries | null | undefined
): string {
    if (grain === "daily") return "PMax quotidienne mesurée"
    if (grain === "monthly") return "PMax mensuelle mesurée"
    return "PMax mesurée"
}

/** Ce qui s'ajoute au titre quand l'heure d'atteinte est SERVIE, et alors seulement. */
export const MENTION_HEURE_DATTEINTE = "heure d'atteinte dans l'infobulle"

/**
 * L'ATTENDU D'UNE PMAX MENSUELLE : un cycle de relève, pas un nombre de jours.
 *
 * Le distributeur la publie au rythme de la relève du compteur. La traduire en
 * « attendu J+n » fabriquerait un retard à chaque fin de mois (complément 7.12).
 */
export const MENTION_PMAX_MENSUELLE =
    "Publiée au cycle de relève du compteur, jamais au jour."

// ============================================================================
// UNE DURÉE SERVIE EN SECONDES SE LIT EN HEURES (écart G11)
// ============================================================================
//
// `duree_depassementpuissanceelec<sens>_turpe` arrive en **secondes** (vérifié
// le 15/09 sur le C2 et le C4 : `meta.unites` dit `s`). Un dépassement de deux
// heures et quart s'écrivait « 8 100 » — un nombre que personne ne lit.
//
// La conversion se fait ICI, une fois, au moment où la série est rangée : le
// tracé, le survol, le tableau et le total héritent tous de la même valeur et
// de la même unité. La convertir au tracé seul aurait laissé le tableau et le
// total en secondes, et deux chiffres pour la même chose sur le même écran.

/** Vrai pour une durée servie en secondes, quelle que soit sa grandeur. */
export function estDureeEnSecondes(
    variable: string,
    unite: string | null
): boolean {
    return unite === "s" && variable.startsWith("duree_")
}

/** L'unité d'une durée, une fois convertie. */
export const UNITE_HEURE = "h"

/** Secondes en heures. `null` reste `null` : une absence n'est jamais un 0. */
export function heuresDepuisSecondes(v: number | null): number | null {
    return typeof v === "number" && Number.isFinite(v) ? v / 3600 : null
}

// ============================================================================
// LES ATTENDUS QUI NE SONT PAS DES JOURS (complément 7.12)
// ============================================================================
//
// Un index est un COMPTEUR CUMULÉ relevé chaque nuit : il témoigne que le
// compteur a communiqué, il ne mesure rien qu'on puisse être « en retard » de
// livrer. Une période de facturation est un STOCK : elle se clôt quand le cycle
// se clôt, pas au bout de n jours.
//
// Écrire « en retard de 72 j » sur l'un ou l'autre serait un verdict fabriqué.
// Le mot vient de cette table, et de nulle part ailleurs : il ne se dérive pas.
export const ATTENDUS_DE_LONGLET: Record<string, string> = {
    index: "témoin",
    facturation: "stock",
}

/** L'attendu non numérique d'un onglet, ou `null` quand il se compte en jours. */
export function attenduDeLOnglet(
    cleOnglet: string | null | undefined
): string | null {
    return (cleOnglet && ATTENDUS_DE_LONGLET[cleOnglet]) || null
}

/**
 * LE CADRAGE DE L'AXE DES ORDONNÉES (écart G9, complément 7.13).
 *
 * Presque toutes les grandeurs de cette page partent de zéro : une énergie, une
 * puissance, une durée s'y lisent par rapport à rien. Un INDEX, non : c'est un
 * cumul à plusieurs millions dont on regarde la PENTE. Ramené à zéro, il devient
 * une ligne plate qui ne dit plus rien (C5 : 2 875 → 2 885 kWh sur trois mois,
 * soit un dixième de pour cent de l'échelle).
 */
export function axeYAuxValeurs(cleOnglet: string | null | undefined): boolean {
    return cleOnglet === "index"
}

/** La phrase servie quand la fenêtre ne contient AUCUNE période close (7.12). */
export const PHRASE_AUCUNE_PERIODE = "Aucune période close dans la fenêtre."

// ============================================================================
// UNE SÉRIE SANS AUCUNE MESURE SUR LA FENÊTRE (DEV-1508 lot 5a, correctif 5a-2)
// ============================================================================
//
// DÉFAUT VU EN PROD le 15/09, sur des compteurs réels : « Énergie réactive »,
// 365 points, AUCUN fini, était quand même remise au moteur de tracé. Elle
// prenait une entrée de légende, consommait une couleur de la palette, et
// laissait le lecteur chercher une courbe qui n'existe pas. Même chose pour le
// minimum et le maximum de la puissance réactive d'un C2, quand seule la
// moyenne était servie.
//
// LA RÈGLE : une absence n'est jamais un 0, et elle n'est jamais une courbe.
// Une série dont aucun point n'est fini SUR LA FENÊTRE REGARDÉE se DIT, en pied
// de carte, avec la pastille de l'absence — elle ne se trace pas.
//
// Trois faits distincts, qui ne se confondent jamais (18ᵉ forme de garde qui
// ment, `top/docs/methode-verification-donnees.md` §12) :
//
//  • le compteur NE PUBLIE PAS la grandeur (`nonPubliee`) : elle n'a jamais de
//    point, quelle que soit la fenêtre. Mot réservé : « Non publié » ;
//  • le compteur la publie, mais RIEN N'A ÉTÉ MESURÉ sur ce qu'on regarde :
//    resserrer ou élargir la fenêtre peut la faire revenir. Mot ci-dessous ;
//  • la LECTURE A ÉCHOUÉ (D11) : on ne sait pas, et cela se dit encore
//    autrement, dans la carte.
//
// La fenêtre est celle qui est REGARDÉE, pas le contexte chargé : le chargement
// déborde d'un quart de chaque côté, et une série vide sur ce qu'on montre l'est
// pour le lecteur, même si un point traîne hors champ.

/** La fenêtre regardée, telle que la vue la tient. `null` = tout le contexte. */
export type FenetreRegardee = { debut: string; fin: string }

/** La mention servie à côté d'une série sans aucune mesure sur la fenêtre. */
export const MENTION_AUCUNE_MESURE = "Aucune mesure sur cette fenêtre"

/**
 * La phrase de la carte quand AUCUNE de ses séries n'a de mesure sur la
 * fenêtre. Elle ne dit pas « ne publie pas » : le compteur publie, et c'est la
 * fenêtre qui est vide.
 */
export const PHRASE_AUCUNE_MESURE = "Aucune mesure sur cette fenêtre."

/**
 * Un point est DANS la fenêtre regardée. Comparaison en chaînes ISO ; la fin est
 * un JOUR, refermé sur sa dernière seconde, sinon un pas de 13 h du dernier jour
 * tomberait hors de la fenêtre qui le montre. Un point de période (`[début,
 * fin]`) est daté par son DÉBUT, comme partout sur cette page.
 */
export function pointDansLaFenetre(
    x: PointX,
    fenetre: FenetreRegardee | null
): boolean {
    if (!fenetre) return true
    const date = Array.isArray(x) ? x[0] : x
    return date >= fenetre.debut && date <= `${fenetre.fin}T23:59:59`
}

/** Les points FINIS d'une série sur la fenêtre. Un `null` ou un `NaN` n'en est pas. */
export function pointsFinisSurLaFenetre(
    serie: SerieTracee,
    fenetre: FenetreRegardee | null
): number {
    let finis = 0
    serie.x.forEach((x, k) => {
        if (!pointDansLaFenetre(x, fenetre)) return
        const v = serie.v[k]
        if (typeof v === "number" && Number.isFinite(v)) finis += 1
    })
    return finis
}

/**
 * Vrai quand la série n'a AUCUNE mesure sur la fenêtre regardée, alors que le
 * compteur publie la grandeur. Une série non publiée rend `false` : elle est
 * déjà dite avec son propre mot, et les deux faits ne se confondent pas.
 */
export function estVideSurLaFenetre(
    serie: SerieTracee,
    fenetre: FenetreRegardee | null
): boolean {
    if (serie.nonPubliee) return false
    return pointsFinisSurLaFenetre(serie, fenetre) === 0
}

// ============================================================================
// AU-DELÀ D'UN AN : L'ÉNERGIE SE LIT EN SEMAINES (lot 5c, complément 7.5)
// ============================================================================
//
// INTENTION : sur deux ans, 730 barres journalières font un peigne illisible,
// large d'un pixel chacune, où un trou de trois jours ne se distingue plus du
// trait entre deux barres. La maquette passe alors à la semaine, et le titre le
// dit : « Énergie active hebdomadaire (kWh) ».
//
// TROIS RÈGLES, ET LA PREMIÈRE COMMANDE LES AUTRES :
//
//  1. UNE SEMAINE SANS AUCUNE MESURE VAUT `null`, JAMAIS 0. Sommer sept `null`
//     rendrait un zéro parfaitement dessiné, et une absence peinte en zéro est
//     exactement ce que l'engagement E2 interdit. La bande, elle, garde le
//     grain du JOUR : les trous continuent de se lire à leur date.
//  2. UNE SEMAINE PARTIELLE SE DIT. Les semaines des deux bouts n'ont pas sept
//     jours dans la fenêtre, et une semaine trouée agrège moins de jours qu'elle
//     n'en porte : `n` compte les jours mesurés, `attendu` ce que la semaine
//     devait porter, `partiel` tranche. Sans cela, une semaine de deux jours se
//     lirait comme une semaine creuse.
//  3. LA VALEUR AGRÉGÉE EST UN CALCUL, pas une mesure. La provenance des points
//     agrégés passe à `computed` et l'origine de la série avec : c'est la même
//     règle que pour tout agrégat fait à la lecture.
//
// Ce qui n'est PAS fait ici : agréger une puissance. Une moyenne de moyennes
// pondérée à l'aveugle n'est pas une puissance moyenne, et un maximum de
// maximums hebdomadaire n'a été demandé par personne. Seule l'énergie, qui
// s'additionne (E2), passe à la semaine.

/** Au-delà de cette largeur regardée, l'énergie se peint en semaines. */
export const SEUIL_SEMAINE_JOURS = 366

/** Le libellé de l'onglet Énergie quand ses barres sont hebdomadaires. */
export const LIBELLE_ENERGIE_HEBDOMADAIRE = "Énergie active hebdomadaire"

/**
 * Vrai quand l'énergie de cet onglet doit se peindre en semaines : l'onglet
 * Énergie, servi au jour, sur une fenêtre de plus d'un an.
 *
 * Les trois conditions comptent. Servi au jour, parce qu'agréger une réponse
 * horaire en semaines cacherait le pas qu'on vient de payer ; l'onglet Énergie,
 * parce que seule une grandeur qui s'additionne se somme (E2) ; plus d'un an,
 * parce qu'en deçà les barres journalières se lisent encore.
 */
export function energieEnSemaines(params: {
    onglet: OngletResolu
    grain?: GrainSeries | null
    joursRegardes?: number | null
}): boolean {
    return (
        params.onglet.cle === "energie" &&
        params.grain === "daily" &&
        typeof params.joursRegardes === "number" &&
        params.joursRegardes > SEUIL_SEMAINE_JOURS
    )
}

/** Le lundi de la semaine d'un jour `AAAA-MM-JJ`, en temps universel. */
export function lundiDeLaSemaine(jour: string): string {
    const d = new Date(`${jour.slice(0, 10)}T00:00:00Z`)
    if (Number.isNaN(d.getTime())) return jour.slice(0, 10)
    const decalage = (d.getUTCDay() + 6) % 7
    d.setUTCDate(d.getUTCDate() - decalage)
    return d.toISOString().slice(0, 10)
}

/**
 * Une série journalière d'énergie, agrégée en semaines. Les autres séries
 * (puissance, index) sont rendues telles quelles : rien ne se somme à l'aveugle.
 */
export function agregerEnSemaines(serie: SerieTracee): SerieTracee {
    if (serie.nonPubliee || serie.x.length === 0) return serie

    type Semaine = {
        lundi: string
        somme: number
        mesures: number
        jours: number
    }
    const semaines = new Map<string, Semaine>()
    serie.x.forEach((x, k) => {
        const jour = (Array.isArray(x) ? x[0] : x).slice(0, 10)
        const lundi = lundiDeLaSemaine(jour)
        const semaine = semaines.get(lundi) ?? {
            lundi,
            somme: 0,
            mesures: 0,
            jours: 0,
        }
        semaine.jours += 1
        const v = serie.v[k]
        if (typeof v === "number" && Number.isFinite(v)) {
            semaine.somme += v
            semaine.mesures += 1
        }
        semaines.set(lundi, semaine)
    })

    const ordonnees = [...semaines.values()].sort((a, b) =>
        a.lundi < b.lundi ? -1 : 1
    )
    return {
        ...serie,
        x: ordonnees.map((s) => s.lundi),
        // Aucune mesure dans la semaine : `null`. Jamais un 0 (E2).
        v: ordonnees.map((s) => (s.mesures > 0 ? s.somme : null)),
        t: ordonnees.map((s) => (s.mesures > 0 ? "computed" : null)),
        n: ordonnees.map((s) => s.mesures),
        partiel: ordonnees.map((s) => s.mesures < s.jours),
        // CE QUI ÉTAIT ALIGNÉ SUR LES JOURS NE L'EST PLUS. L'heure d'atteinte
        // et la nature d'une période sont des faits du POINT servi : recopiées
        // telles quelles sur des semaines, elles désigneraient un autre point
        // que le leur. On les laisse tomber plutôt que de les faire mentir.
        at: null,
        natureParPoint: null,
        attendu: 7,
        origine: "calcul",
        origines: origineDesPoints(
            ordonnees.map((s) => (s.mesures > 0 ? "computed" : null))
        ),
    }
}

// ============================================================================
// LE MAXIMUM DES PUISSANCES MOYENNES (décision de Mathieu du 11/09/2026)
// ============================================================================
//
// La puissance maximale INSTANTANÉE n'existe qu'au grain journalier, et c'est
// normal : c'est le compteur qui la relève. Au niveau horaire, ce qui se montre
// est le MAXIMUM DES PUISSANCES MOYENNES sur le pas du compteur — une autre
// grandeur, en kW, qui vaut toujours moins ou autant. Les confondre ferait lire
// un dépassement là où il n'y en a pas : le libellé le dit, et la mention aussi.

/** Vrai pour une série de puissance active, quel que soit le sens. */
function estPuissanceActive(variable: string): boolean {
    return variable.startsWith("puissanceactive_")
}

/**
 * Vrai quand la série servie EST le maximum des puissances moyennes. Deux
 * façons de le savoir, et la première fait foi : la NATURE servie par le
 * lecteur. À défaut (le lecteur ne la sert pas encore), le niveau horaire et la
 * statistique suffisent, parce que l'instantanée n'y est pas publiée.
 */
export function estMaxDesMoyennes(params: {
    variable: string
    stat: StatSeries
    grain?: GrainSeries | null
    nature?: NatureSeries | null
}): boolean {
    if (params.nature?.startsWith("max_moyennes_")) return true
    return (
        params.stat === "max" &&
        params.grain === "hourly" &&
        estPuissanceActive(params.variable)
    )
}

/** Le pas écrit dans la nature (`max_moyennes_30min_active` → `30 min`). */
function pasDeLaNature(nature: NatureSeries | null | undefined): string | null {
    const trouve = /^max_moyennes_(\d+)min_active$/.exec(nature ?? "")
    return trouve ? `${trouve[1]} min` : null
}

/** Le pas du compteur, écrit comme on le lit : « 30 min ». */
function pasLisible(minutes: number | null | undefined): string | null {
    return minutes && minutes > 0 ? `${minutes} min` : null
}

/** Le libellé de la série, qui DIT la grandeur au lieu de la laisser deviner. */
export function libelleMaxDesMoyennes(pas: string | null): string {
    return pas
        ? `Puissance moyenne maximale sur ${pas}`
        : "Puissance moyenne maximale sur le pas du compteur"
}

/**
 * La mention servie à côté. Elle nomme les deux grandeurs et dit où trouver
 * l'autre : sans elle, un client cherche une instantanée qui n'est pas là.
 */
export const MENTION_MAX_DES_MOYENNES =
    "Il s'agit du maximum des puissances moyennes sur le pas du compteur, et non de la puissance maximale instantanée, disponible au grain journalier."

/** Clé de lecture des métadonnées : `variable.stat`, jamais la variable seule. */
export function cleMeta(variable: string, stat: StatSeries): string {
    return `${variable}.${stat}`
}

/** L'ordre de lecture des statistiques, pour que la phrase soit toujours la même. */
const ORDRE_STATS: StatSeries[] = ["mean", "min", "max", "sum", "last"]

/**
 * La phrase servie à la place d'une statistique omise sous plafond.
 *
 * Elle dit DEUX choses et pas une de plus : de quoi il s'agit, et jusqu'où le
 * calcul se fait. Aucun mot de notre plomberie, aucun nom de société, et aucun
 * nombre inventé quand le lecteur n'a pas chiffré la fenêtre.
 */
export function mentionPlafond(
    stats: StatSeries[],
    plafondJours?: number | null
): string {
    const mots = stats.map((s) => LIBELLES_STAT[s]).join(" et ")
    const enveloppe =
        stats.length > 0 && stats.every((s) => s === "min" || s === "max")
    const sujet = enveloppe ? `Enveloppe ${mots}` : `Valeur ${mots}`
    return plafondJours
        ? `${sujet} : calcul limité à ${plafondJours} jours d'historique, non servie sur cette fenêtre.`
        : `${sujet} : calcul non servi sur cette fenêtre.`
}

/**
 * Les statistiques omises sous plafond, groupées par série : le minimum et le
 * maximum d'une même courbe font UNE mention, pas deux.
 *
 * Une statistique que le lecteur a pourtant servie n'est jamais annoncée
 * manquante : ce qui est tracé se lit, ce qui manque se dit.
 */
export function seriesPlafonnees(params: {
    onglet: OngletResolu
    parDirection: Array<{
        direction: DirectionEnedis
        reponses: ReponseSeries[]
    }>
}): SeriePlafonnee[] {
    const mentions: SeriePlafonnee[] = []

    for (const { direction, reponses } of params.parDirection) {
        const parVariable = new Map<string, SerieOnglet>(
            params.onglet.series
                .filter((s) => s.direction === direction)
                .map((s) => [s.variable, s])
        )
        const servies = new Set<string>()
        for (const reponse of reponses) {
            for (const serie of reponse.data ?? []) {
                servies.add(cleMeta(serie.variable, serie.stat))
            }
        }

        const groupes = new Map<
            string,
            {
                declaree: SerieOnglet
                stats: Set<StatSeries>
                plafondJours: number | null
            }
        >()
        for (const reponse of reponses) {
            const applique: Record<string, PlafondApplique> =
                reponse.meta?.plafond_applique ?? {}
            for (const [cle, motif] of Object.entries(applique)) {
                const coupure = cle.lastIndexOf(".")
                if (coupure < 0) continue
                const variable = cle.slice(0, coupure)
                const stat = cle.slice(coupure + 1) as StatSeries
                if (!(stat in LIBELLES_STAT)) continue
                if (servies.has(cle)) continue
                const declaree = parVariable.get(variable)
                if (!declaree) continue
                const plafondJours = motif?.plafond_jours ?? null
                const cleGroupe = `${declaree.cle}-${plafondJours ?? "sans"}`
                const groupe = groupes.get(cleGroupe) ?? {
                    declaree,
                    stats: new Set<StatSeries>(),
                    plafondJours,
                }
                groupe.stats.add(stat)
                groupes.set(cleGroupe, groupe)
            }
        }

        for (const [cleGroupe, groupe] of groupes) {
            const stats = ORDRE_STATS.filter((s) => groupe.stats.has(s))
            mentions.push({
                cle: `plafond-${cleGroupe}-${direction}`,
                variable: groupe.declaree.variable,
                direction,
                stats,
                libelle: groupe.declaree.libelle,
                texte: mentionPlafond(stats, groupe.plafondJours),
            })
        }
    }

    return mentions
}

/**
 * Les séries à peindre pour un onglet, à partir de ce que le lecteur a rendu.
 *
 * Une série demandée que le lecteur n'a pas rendue n'est PAS inventée : elle
 * n'apparaît que si le compteur la déclare non publiée (mention), sinon elle
 * n'existe pas dans cet onglet.
 */
export function seriesTracees(params: {
    onglet: OngletResolu
    parDirection: Array<{
        direction: DirectionEnedis
        reponses: ReponseSeries[]
    }>
    /** Le niveau regardé. Il décide de ce que le maximum veut dire. */
    grain?: GrainSeries | null
    /** Le pas de la courbe du compteur, en minutes. Lu, jamais deviné. */
    minutesPasCompteur?: number | null
}): SerieTracee[] {
    const tracees: SerieTracee[] = []

    for (const { direction, reponses } of params.parDirection) {
        const attendues = params.onglet.series.filter(
            (s) => s.direction === direction
        )
        const parVariable = new Map<string, SerieOnglet>(
            attendues.map((s) => [s.variable, s])
        )
        const vues = new Set<string>()

        for (const reponse of reponses) {
            for (const serie of reponse.data ?? []) {
                const declaree = parVariable.get(serie.variable)
                if (!declaree) continue
                const cle = cleMeta(serie.variable, serie.stat)
                vues.add(serie.variable)
                const calculeeALaLecture = Boolean(
                    reponse.meta?.calcule_a_la_lecture?.[cle]
                )
                const nature = reponse.meta?.natures?.[cle] ?? null
                const grain = serie.grain ?? params.grain ?? null
                // L'ORIGINE SE LIT DES POINTS. Le drapeau de série et l'origine
                // déclarée par la carte ne servent plus que de repli, quand la
                // réponse ne porte aucune provenance : `meta.calcule_a_la_lecture`
                // ne suffit plus jamais seul (décision du 11/09).
                // Un agrégat fait À LA LECTURE n'est pas une mesure, même quand
                // les points qu'il agrège en sont : la valeur servie est le
                // produit d'un calcul. Idem pour une série que la carte déclare
                // dérivée (la réactive totale, l'apparente). Le drapeau ne
                // DÉCIDE plus seul de l'origine — il requalifie la provenance,
                // et le reste de la règle (couverture, correction) s'applique
                // ensuite comme pour n'importe quel calcul.
                const derivee =
                    calculeeALaLecture || declaree.origine === "calcul"
                const provenances = (serie.t ?? []).map((provenance) =>
                    derivee && provenance === "raw"
                        ? ("computed" as ProvenanceSeries)
                        : provenance
                )
                const origines = origineDesPoints(
                    provenances,
                    reponse.meta?.n?.[cle],
                    reponse.meta?.partiel?.[cle],
                    attenduParPoint(
                        (grain ?? "daily") as GrainSeries,
                        params.minutesPasCompteur
                    )
                )
                const origineDeRepli: OrigineSerie =
                    declaree.origine === "calcul" || calculeeALaLecture
                        ? "calcul"
                        : declaree.origine
                const maxDesMoyennes = estMaxDesMoyennes({
                    variable: serie.variable,
                    stat: serie.stat,
                    grain,
                    nature,
                })
                const pmax = estPmaxMesuree(nature)
                const libelleBase = pmax
                    ? libellePmaxMesuree(grain)
                    : maxDesMoyennes
                      ? libelleMaxDesMoyennes(
                            pasDeLaNature(nature) ??
                                pasLisible(params.minutesPasCompteur)
                        )
                      : declaree.libelle
                // L'UNITÉ SERVIE FAIT FOI, sauf quand on la CONVERTIT : une
                // durée en secondes se lit en heures, et l'unité suit la valeur.
                const uniteServie =
                    reponse.meta?.unites?.[cle] ?? declaree.unite
                const enHeures = estDureeEnSecondes(serie.variable, uniteServie)
                const valeurs = (serie.v ?? []).map((v) =>
                    enHeures ? heuresDepuisSecondes(v) : v
                )
                tracees.push({
                    cle: `${declaree.cle}-${serie.stat}`,
                    libelle:
                        !maxDesMoyennes && !pmax && declaree.stats.length > 1
                            ? `${libelleBase} (${LIBELLES_STAT[serie.stat]})`
                            : libelleBase,
                    libelleBase,
                    // La famille vient de la CARTE des onglets, pas d'un
                    // découpage du libellé : « Durée de dépassement heures de
                    // pointe » se couperait au mauvais mot.
                    libelleFamille: declaree.libelleFamille,
                    variable: serie.variable,
                    stat: serie.stat,
                    direction,
                    unite: enHeures ? UNITE_HEURE : uniteServie,
                    nature,
                    origine: origineDominante(origines) ?? origineDeRepli,
                    origines,
                    mention: maxDesMoyennes
                        ? MENTION_MAX_DES_MOYENNES
                        : pmax && grain === "monthly"
                          ? MENTION_PMAX_MENSUELLE
                          : null,
                    x: serie.x ?? [],
                    v: valeurs,
                    t: serie.t ?? [],
                    at: reponse.meta?.at?.[cle] ?? null,
                    natureParPoint: reponse.meta?.nature?.[cle] ?? null,
                    pmaxMesuree: pmax,
                    n: reponse.meta?.n?.[cle] ?? null,
                    partiel: reponse.meta?.partiel?.[cle] ?? null,
                    attendu: attenduParPoint(
                        (grain ?? "daily") as GrainSeries,
                        params.minutesPasCompteur
                    ),
                    grainDeclare: grain
                        ? declaree.grains.includes(grain)
                        : undefined,
                    nonPubliee: false,
                })
            }
        }

        // Ce que le compteur ne publie pas : une mention, à sa place, sans trace.
        for (const reponse of reponses) {
            for (const [variable, qualite] of Object.entries(
                reponse.qualite ?? {}
            )) {
                if (qualite?.etat !== "non_publiee") continue
                if (vues.has(variable)) continue
                const declaree = parVariable.get(variable)
                if (!declaree) continue
                vues.add(variable)
                tracees.push({
                    cle: `${declaree.cle}-non-publiee`,
                    libelle: declaree.libelle,
                    libelleBase: declaree.libelle,
                    libelleFamille: declaree.libelleFamille,
                    variable,
                    stat: declaree.stats[0] ?? "mean",
                    direction,
                    unite: declaree.unite,
                    nature: null,
                    // Aucun point, donc aucune origine LUE : c'est celle que la
                    // carte déclare qui s'écrit, faute de mieux, et les parts
                    // restent vides plutôt qu'inventées à 100 %.
                    origine: declaree.origine,
                    origines: origineDesPoints([]),
                    mention: null,
                    x: [],
                    v: [],
                    t: [],
                    at: null,
                    natureParPoint: null,
                    pmaxMesuree: false,
                    n: null,
                    partiel: null,
                    attendu: null,
                    nonPubliee: true,
                })
            }
        }
    }

    return fusionnerClassesSynonymes(tracees)
}

/**
 * Les graphes de l'onglet : un par sens et par unité.
 *
 * Le regroupement PAR UNITÉ est ce qui interdit de mettre des kVA et des kW sur
 * le même axe. Une série sans unité connue reste seule dans son graphe plutôt
 * que d'être posée sur l'échelle d'une autre.
 */
export function graphesDeLOnglet(params: {
    onglet: OngletResolu
    parDirection: Array<{
        direction: DirectionEnedis
        reponses: ReponseSeries[]
    }>
    /** Le niveau regardé, qui décide de ce que le maximum veut dire. */
    grain?: GrainSeries | null
    /** Le pas de la courbe du compteur, en minutes. Lu, jamais deviné. */
    minutesPasCompteur?: number | null
    /**
     * La largeur REGARDÉE, en jours. Au-delà d'un an, l'énergie journalière se
     * peint en semaines (complément 7.5 du plan du 15/09). `null` = inconnue,
     * et rien n'est agrégé : on n'agrège pas sur une largeur devinée.
     */
    joursRegardes?: number | null
}): GrapheEnedis[] {
    const hebdomadaire = energieEnSemaines({
        onglet: params.onglet,
        grain: params.grain,
        joursRegardes: params.joursRegardes,
    })
    const series = seriesTracees(params).map((serie) =>
        hebdomadaire ? agregerEnSemaines(serie) : serie
    )
    // LE MIROIR NE VAUT QUE POUR L'ÉNERGIE ET LA PUISSANCE (décision Q13 du
    // 17/09). Un index est un cumul qui ne se renverse pas, une période de
    // facturation et un dépassement se lisent classe par classe : ces trois-là
    // se rangent en DEUX CARTES CÔTE À CÔTE, une par sens.
    const miroir =
        params.parDirection.length > 1 &&
        (params.onglet.cle === "energie" || params.onglet.cle === "puissance")
    const groupes = new Map<string, GrapheEnedis>()

    for (const serie of series) {
        const cleUnite = serie.unite ?? "sans-unite"
        // LA PMAX RELEVÉE OUVRE SA PROPRE CARTE (écart G6). Le regroupement par
        // unité ne suffit PAS à l'isoler : la puissance apparente dérivée est
        // elle aussi en kVA, et les deux se seraient retrouvées sur un axe
        // commun alors que l'une est relevée et l'autre calculée.
        // LE MIROIR RÉUNIT LES DEUX SENS SUR UNE FIGURE (lot 5g) : la clé de
        // regroupement oublie alors le sens, et l'unité seule range. La carte
        // de PMax relevée garde la sienne, par sens : un nuage de points de
        // pointes n'a pas de miroir à faire.
        const cle = serie.pmaxMesuree
            ? `${params.onglet.cle}-${serie.direction}-${cleUnite}-pmax`
            : miroir
              ? `${params.onglet.cle}-${cleUnite}-miroir`
              : `${params.onglet.cle}-${serie.direction}-${cleUnite}`
        const existant = groupes.get(cle)
        if (existant) {
            existant.series.push(serie)
            continue
        }
        groupes.set(cle, {
            cle,
            // Le titre a besoin de TOUTES les séries du graphe (la grandeur ne
            // s'écrit que s'il n'en porte qu'une, l'enveloppe qu'à trois
            // statistiques) : il se pose à la fin, jamais sur la première série.
            titre: "",
            unite: serie.unite,
            direction: serie.direction,
            miroir: miroir && !serie.pmaxMesuree,
            series: [serie],
            plafonnees: [],
        })
    }

    // Une statistique omise se dit SUR le graphe de sa série. À défaut, sur le
    // premier graphe de ce sens ; et si ce sens n'a rien du tout à montrer, elle
    // ouvre le sien, pour n'être perdue nulle part.
    for (const mention of seriesPlafonnees(params)) {
        // LA CARTE DE PMAX N'ACCUEILLE AUCUNE MENTION DE PLAFOND. Sur un C5, la
        // statistique omise (`min`) et la PMax relevée portent le MÊME nom de
        // variable : sans ce filtre, « Enveloppe minimum : calcul limité … »
        // atterrissait sur la carte de la PMax, qui n'a pas d'enveloppe du tout.
        // Une figure en miroir porte les DEUX sens : c'est ce qu'elle montre,
        // pas son champ `direction`, qui dit où sa mention doit se poser.
        const graphes = [...groupes.values()].filter(
            (g) =>
                (g.miroir
                    ? g.series.some((s) => s.direction === mention.direction)
                    : g.direction === mention.direction) &&
                !g.series.some((s) => s.pmaxMesuree)
        )
        const cible =
            graphes.find((g) =>
                g.series.some((s) => s.variable === mention.variable)
            ) ?? graphes[0]
        if (cible) {
            cible.plafonnees.push(mention)
            continue
        }
        const declaree = params.onglet.series.find(
            (s) =>
                s.variable === mention.variable &&
                s.direction === mention.direction
        )
        const unite = declaree?.unite ?? null
        const cle = `${params.onglet.cle}-${mention.direction}-${unite ?? "sans-unite"}`
        groupes.set(cle, {
            cle,
            titre: "",
            unite,
            direction: mention.direction,
            series: [],
            plafonnees: [mention],
        })
    }

    // LA PMAX RELEVÉE, POSÉE EN POINTS SUR LA COURBE DU MÊME SENS (7.11).
    //
    // Au niveau JOURNALIER seulement : c'est là que les deux figures parlent du
    // même objet, une journée, et qu'on veut lire d'un coup d'oeil où la pointe
    // est tombée par rapport à ce que la journée a consommé en moyenne. Au pas
    // natif et à l'heure, la courbe a des milliers de points et la PMax douze :
    // les poser ensemble ferait douze pastilles perdues sur un mur de trait.
    //
    // La carte de PMax reste, avec ses chiffres et son heure d'atteinte : ceci
    // est une superposition de lecture, pas un déménagement.
    const grainServi = hebdomadaire ? "weekly" : (params.grain ?? null)
    if (grainServi === "daily") {
        for (const graphe of groupes.values()) {
            if (graphe.series.some((s) => s.pmaxMesuree)) continue
            // PAS DE PMAX POSÉE SUR UN MIROIR : la figure porte déjà deux sens
            // de part et d'autre de zéro, et un nuage de points sur un second
            // axe y ajouterait une troisième lecture illisible.
            if (graphe.miroir) continue
            if (!graphe.series.some((s) => estPuissanceActive(s.variable))) {
                continue
            }
            const pmax = series.find(
                (s) => s.pmaxMesuree && s.direction === graphe.direction
            )
            if (pmax) graphe.pmaxPosee = pmax
        }
    }

    // LE TITRE, UNE FOIS LES SÉRIES RANGÉES : il dit la grandeur quand le graphe
    // n'en porte qu'une, le grain quand la grandeur l'admet, et « Enveloppe »
    // quand c'est ce que la figure montre (G3, G5).
    for (const graphe of groupes.values()) {
        graphe.titre = titreDuGraphe(
            params.onglet,
            graphe.direction,
            graphe.unite,
            params.parDirection.length > 1 && !graphe.miroir,
            hebdomadaire,
            {
                miroir: graphe.miroir,
                grain: grainServi,
                grandeur: grandeurDuGraphe(graphe.series),
                enveloppe: porteUneEnveloppe(graphe.series),
                pmaxMesuree: graphe.series.some((s) => s.pmaxMesuree),
                heureDAtteinte: graphe.series.some(
                    (s) => s.pmaxMesuree && (s.at?.length ?? 0) > 0
                ),
                pmaxPosee: graphe.pmaxPosee
                    ? (graphe.pmaxPosee.unite ?? null)
                    : null,
                famille: familleDuGraphe(graphe.series),
                classes: classesDuGraphe(graphe.series),
            }
        )
    }

    // LA CARTE DE PMAX PASSE EN DERNIER, toujours. La première carte d'un
    // panneau porte la puce de niveau et le bouton du pic (`porteLeNiveau`) : si
    // la PMax ouvrait le panneau — et elle le faisait, le lecteur servant le
    // maximum avant la moyenne — ces deux commandes se seraient posées sur la
    // figure qu'elles ne commandent pas.
    const rangees = [...groupes.values()]
    return [
        ...rangees.filter((g) => !g.series.some((s) => s.pmaxMesuree)),
        ...rangees.filter((g) => g.series.some((s) => s.pmaxMesuree)),
    ]
}

// ============================================================================
// L'ENVELOPPE : UNE GRANDEUR, TROIS STATISTIQUES, UNE SEULE CHOSE À LIRE (5d, G5)
// ============================================================================
//
// Ce qui était servi jusqu'ici : trois COURBES distinctes, « (moyenne) »,
// « (minimum) », « (maximum) », de la même couleur, séparées par un motif de
// trait. Trois entrées de légende, trois lignes à suivre de l'œil, pour une
// seule information : entre quelles bornes la puissance a varié, et où elle
// s'est tenue en moyenne.
//
// La maquette en fait UNE chose : une aire entre le minimum et le maximum, et
// la moyenne tracée dedans. Le regroupement se décide ICI, sur les faits
// (quelle grandeur porte quelles statistiques), et le tracé le suit.
//
// ⚠️ L'aire n'existe QUE si le minimum ET le maximum sont servis. Avec un seul
// des deux, il n'y a pas d'intervalle : remplir jusqu'à l'axe peindrait une
// surface qui ne veut rien dire, et jusqu'à la moyenne raconterait une
// dissymétrie inventée. Les séries restent alors des courbes.

/** Une grandeur et les statistiques servies pour elle, sur un même graphe. */
export type GroupeEnveloppe = {
    /** `variable|direction` : deux sens ne se mêlent jamais. */
    cle: string
    libelleBase: string
    mean: SerieTracee
    min: SerieTracee | null
    max: SerieTracee | null
}

/**
 * Les groupes (moyenne, minimum, maximum) d'une liste de séries.
 *
 * Seuls les groupes qui portent une MOYENNE et au moins une borne en font
 * partie : une moyenne seule est une courbe ordinaire, et deux bornes sans
 * moyenne ne dessinent pas une enveloppe à lire.
 */
export function groupesDEnveloppe(series: SerieTracee[]): GroupeEnveloppe[] {
    const parGrandeur = new Map<string, SerieTracee[]>()
    for (const serie of series) {
        const cle = `${serie.variable}|${serie.direction}`
        parGrandeur.set(cle, [...(parGrandeur.get(cle) ?? []), serie])
    }
    const groupes: GroupeEnveloppe[] = []
    for (const [cle, lot] of parGrandeur) {
        const mean = lot.find((s) => s.stat === "mean")
        const min = lot.find((s) => s.stat === "min") ?? null
        const max = lot.find((s) => s.stat === "max") ?? null
        if (!mean || (!min && !max)) continue
        groupes.push({ cle, libelleBase: mean.libelleBase, mean, min, max })
    }
    return groupes
}

/**
 * Vrai quand ce graphe porte une enveloppe complète DE PUISSANCE ACTIVE.
 *
 * C'est cette enveloppe-là, et elle seule, qui prend le titre de sa carte
 * (« Enveloppe horaire (kW) », maquette v1.5). Les autres grandeurs dessinent
 * leur aire de la même façon, mais gardent LEUR nom dans le titre : deux cartes
 * intitulées « Enveloppe horaire », l'une en kW et l'autre en kVAr, ne se
 * distingueraient plus que par leur unité — on aurait perdu la grandeur.
 */
export function porteUneEnveloppe(series: SerieTracee[]): boolean {
    return groupesDEnveloppe(series).some(
        (g) =>
            g.min !== null &&
            g.max !== null &&
            estPuissanceActive(g.mean.variable)
    )
}

/**
 * DE QUOI L'ENVELOPPE EST CALCULÉE (revue du 17/09, point R4).
 *
 * MOT POUR MOT : « l'enveloppe horaire est calculée mais il faut dire que c'est
 * calculé à partir de la courbe de charge ». La pastille d'origine écrivait
 * « Calculé » et s'arrêtait là ; un lecteur pouvait y voir un modèle, une
 * estimation, n'importe quel calcul. La moyenne, le minimum et le maximum d'un
 * pas agrégé sont dérivés des points de la COURBE DE CHARGE de ce compteur, et
 * c'est ce fait qui manquait.
 *
 * LE PAS NE SE DEVINE PAS. Il vient du pas du compteur, lu dans le référentiel
 * (`minutesPasCompteur`). Sans lui, la phrase s'arrête à la courbe de charge :
 * écrire « au pas de 30 min » sur un compteur qu'on n'a pas lu serait un
 * chiffre inventé sur une surface client.
 *
 * `null` = il n'y a rien à dire : la carte n'est pas une enveloppe, ou son
 * origine lue n'est pas un calcul.
 */
export const COMPLEMENT_COURBE_DE_CHARGE = "à partir de la courbe de charge"

export function complementOrigineEnveloppe(params: {
    series: SerieTracee[]
    origine: OrigineSerie | null
    /** Le pas du compteur en minutes, LU. `null` = inconnu, et il se tait. */
    minutesPasCompteur?: number | null
}): string | null {
    if (params.origine !== "calcul") return null
    // LA CONDITION EST « UN AGRÉGAT », PAS « UNE AIRE PEINTE ».
    // `porteUneEnveloppe` dit si la figure DESSINE une aire, et ne le dit que
    // pour la puissance active. Ce qui compte ici est autre chose : une moyenne
    // accompagnée d'une borne est un agrégat dérivé des points de la courbe, et
    // la réactive comme l'apparente le sont autant que l'active (R7).
    if (groupesDEnveloppe(params.series).length === 0) return null
    const pas = pasLisible(params.minutesPasCompteur)
    return pas
        ? `${COMPLEMENT_COURBE_DE_CHARGE} au pas de ${pas}`
        : COMPLEMENT_COURBE_DE_CHARGE
}

/**
 * Le nom d'une enveloppe dans la légende : la grandeur, et ce qu'elle porte.
 * Il nomme les trois statistiques une fois, au lieu de trois entrées.
 */
export function libelleDeLEnveloppe(groupe: GroupeEnveloppe): string {
    const bornes = [
        "moyenne",
        ...(groupe.min ? ["min"] : []),
        ...(groupe.max ? ["max"] : []),
    ]
    return `${groupe.libelleBase} (enveloppe : ${bornes.join(", ")})`
}

// ============================================================================
// LE TITRE D'UN GRAPHE, AU GRAIN SERVI (5d, écart G3, complément 7.4)
// ============================================================================
//
// « Énergie (kWh) » ne dit pas ce qu'on regarde : une barre par jour, une par
// heure et une par semaine s'écrivaient toutes les trois sous ce titre-là. La
// maquette met le grain dans le titre — « Énergie active journalière (kWh) » —
// et le complément 7.4 du plan l'autorise explicitement.
//
// Deux précautions, et elles décident du code :
//
//  • LA GRANDEUR VIENT DES SÉRIES, pas de l'onglet. L'onglet « Énergie » porte
//    l'énergie active ET l'énergie réactive ; quand elles partagent une unité,
//    elles partagent un graphe, et écrire « Énergie active journalière » sur un
//    graphe qui porte les deux serait faux. La grandeur ne s'écrit donc que
//    lorsque le graphe n'en porte qu'une.
//  • LE GRAIN NE S'ÉCRIT QUE LÀ OÙ IL SE DIT. Une énergie est journalière ou
//    horaire ; une puissance non agrégée n'a pas de qualificatif de ce genre, et
//    son grain se lit dans l'indicateur de Niveau, à côté de la fenêtre.

/** Le qualificatif de grain d'une grandeur féminine (énergie). `""` ailleurs. */
function qualificatifDeGrain(grain: GrainSeries | null | undefined): string {
    if (grain === "daily") return " journalière"
    if (grain === "hourly") return " horaire"
    if (grain === "weekly") return " hebdomadaire"
    return ""
}

/** Ce que le titre d'un graphe doit savoir, au-delà de l'onglet et de l'unité. */
export type ContexteDuTitre = {
    /** Le niveau SERVI. Il s'écrit dans le titre quand la grandeur l'admet. */
    grain?: GrainSeries | null
    /** La grandeur, quand le graphe n'en porte qu'une. `null` = l'onglet parle. */
    grandeur?: string | null
    /** Vrai quand le graphe porte une enveloppe complète (min et max servis). */
    enveloppe?: boolean
    /** Vrai quand CE graphe EST la carte de la PMax relevée (G6). */
    pmaxMesuree?: boolean
    /** Vrai quand le lecteur sert l'heure d'atteinte de cette PMax. */
    heureDAtteinte?: boolean
    /** L'unité de la PMax POSÉE en points sur ce graphe, ou `null` (7.11). */
    pmaxPosee?: string | null
    /**
     * La famille commune des séries, sans leur classe temporelle (5d3).
     * `null` quand elles n'en partagent pas une seule.
     */
    famille?: string | null
    /** Combien de classes temporelles distinctes ce graphe porte (5d3). */
    classes?: number
    /** Vrai quand la figure porte les deux sens en miroir (lot 5g). */
    miroir?: boolean
}

/** Ce qu'une figure en miroir dit d'elle-même, en toutes lettres (lot 5g). */
export const MENTION_MIROIR =
    "soutirage vers le haut, injection vers le bas, jamais de net"

/** La famille partagée par toutes les séries d'un graphe, ou `null`. */
export function familleDuGraphe(series: SerieTracee[]): string | null {
    const familles = new Set(series.map((s) => s.libelleFamille))
    return familles.size === 1 ? [...familles][0] : null
}

/** Combien de grandeurs distinctes (donc de classes) ce graphe peint. */
export function classesDuGraphe(series: SerieTracee[]): number {
    return new Set(series.map((s) => s.libelleBase)).size
}

/**
 * Le titre d'un graphe. Le sens s'écrit dès qu'il n'est PAS le soutirage (lot
 * 5g) : sur un compteur qui ne fait que soutirer, le répéter n'apprend rien,
 * mais taire l'injection laisse lire une consommation là où il y a une
 * production. Les deux sens regardés ensemble l'écrivent donc aussi.
 */
export function titreDuGraphe(
    onglet: OngletResolu,
    direction: DirectionEnedis,
    unite: string | null,
    deuxSens: boolean,
    /** Vrai quand l'énergie est peinte en semaines : le titre le DIT (7.5). */
    hebdomadaire = false,
    contexte: ContexteDuTitre = {}
): string {
    const grandeur = contexte.grandeur ?? null
    const ecrireLeSens = deuxSens || direction === "injection"
    let base: string
    if (contexte.pmaxMesuree) {
        // LA CARTE DE LA PMAX RELEVÉE. Son titre porte le grain (une PMax
        // quotidienne et une PMax mensuelle ne se relèvent pas au même rythme)
        // et, quand le lecteur la sert, l'existence de l'heure d'atteinte : sans
        // elle, personne n'irait survoler un point pour la chercher.
        base = libellePmaxMesuree(contexte.grain)
        const titre = unite ? `${base} (${unite})` : base
        const morceauxPmax = [titre]
        if (ecrireLeSens) morceauxPmax.push(LIBELLES_DIRECTION[direction])
        return contexte.heureDAtteinte
            ? `${morceauxPmax.join(" · ")}, ${MENTION_HEURE_DATTEINTE}`
            : morceauxPmax.join(" · ")
    }
    if (onglet.cle === "puissance" && contexte.enveloppe) {
        // L'enveloppe est ce que la carte MONTRE : elle prend le titre, et la
        // grandeur se relit dans la légende, qui la nomme.
        base =
            contexte.grain === "hourly"
                ? "Enveloppe horaire"
                : contexte.grain === "daily"
                  ? "Enveloppe journalière"
                  : "Enveloppe"
    } else if (hebdomadaire) {
        base = grandeur
            ? `${grandeur} hebdomadaire`
            : LIBELLE_ENERGIE_HEBDOMADAIRE
    } else if (grandeur && onglet.cle === "energie") {
        base = `${grandeur}${qualificatifDeGrain(contexte.grain)}`
    } else if (onglet.cle === "facturation" && contexte.famille) {
        // UNE PÉRIODE DE FACTURATION N'EST PAS UN JOUR (G10). Le titre dit ce
        // que l'axe porte — des périodes, et non des dates — et prévient qu'il
        // y a plusieurs classes côte à côte quand c'est le cas. Sur une classe
        // unique, c'est ELLE qui se nomme : la famille seule serait plus vague
        // que ce que la figure montre.
        base =
            (contexte.classes ?? 1) > 1
                ? `${contexte.famille} par période et par classe`
                : `${grandeur ?? contexte.famille} par période`
    } else if (
        onglet.cle === "depassements" &&
        contexte.famille &&
        (contexte.classes ?? 1) > 1
    ) {
        // La grandeur une fois, les classes dans la légende (G11).
        base = contexte.famille
    } else {
        base = grandeur ?? onglet.libelle
    }
    const morceaux = [base]
    if (ecrireLeSens) morceaux.push(LIBELLES_DIRECTION[direction])
    const titre = morceaux.join(" · ")
    let avecUnite = unite ? `${titre} (${unite})` : titre
    // LE MIROIR DIT CE QU'IL MONTRE, et surtout ce qu'il NE montre pas : des
    // barres sous l'axe ne se lisent pas toutes seules, et « jamais de net »
    // coupe court à la seule mauvaise lecture possible (E5).
    if (contexte.miroir) {
        avecUnite = `${avecUnite} : ${MENTION_MIROIR}`
    }
    if (onglet.cle === "depassements" && (contexte.classes ?? 1) > 1) {
        avecUnite = `${avecUnite} par classe temporelle`
    }
    // LA PMAX POSÉE EN POINTS SE DIT DANS LE TITRE, avec SON unité (7.11) : deux
    // grandeurs sur une figure, c'est le titre qui doit les nommer toutes les
    // deux, sinon l'échelle de droite est un mystère.
    return contexte.pmaxPosee
        ? `${avecUnite} et PMax mesurée en points (${contexte.pmaxPosee})`
        : avecUnite
}

/** La grandeur d'un graphe, quand il n'en porte qu'une. `null` sinon. */
export function grandeurDuGraphe(series: SerieTracee[]): string | null {
    const noms = new Set(series.map((s) => s.libelleBase))
    return noms.size === 1 ? [...noms][0] : null
}

/**
 * Comment se peint un onglet. Une grandeur de période (énergie, dépassements,
 * facturation) se lit en barres, une grandeur instantanée en courbe, un index en
 * points reliés.
 */
export function renduDeLOnglet(onglet: string): "barres" | "courbe" {
    return onglet === "puissance" || onglet === "index" ? "courbe" : "barres"
}

/** Vrai pour une grandeur qui s'additionne : le tableau lui donne un total. */
export function estSommable(onglet: string): boolean {
    return onglet !== "puissance" && onglet !== "index"
}

// ============================================================================
// CE QUI SE TOTALISE SUR UNE FENÊTRE (DEV-1508 lot B4)
// ============================================================================
//
// Un total posé en tête de la carte du graphe répond à une question précise :
// « combien, sur ce que je regarde ». Cette question n'a de sens que pour une
// grandeur d'ÉNERGIE, qui s'accumule dans le temps.
//
//  • une PUISSANCE ne s'additionne pas : additionner des kW de pas en pas rend
//    un nombre qui ne nomme rien, et qui grandit quand le pas se raffine ;
//  • un INDEX est déjà un cumul : le sommer compterait chaque kWh autant de fois
//    qu'il reste au compteur ;
//  • une DURÉE de dépassement s'additionne arithmétiquement, mais son total
//    n'est pas une énergie et ne se lit pas dans l'unité du graphe comme une
//    quantité consommée. Elle reste hors de ce bloc (décision du brief B4).
//
// La règle prend l'onglet ET l'unité du graphe, et non l'onglet seul : l'onglet
// « Dépassements » porte à la fois une durée (en secondes) et une énergie (en
// kWh), sur deux graphes distincts puisqu'un graphe ne mêle jamais deux unités.
// Décider à l'onglet aurait donc totalisé des secondes.

/**
 * Les unités d'énergie, écrites comme le lecteur les sert, comparées sans
 * casse. La liste est FERMÉE : une unité inconnue ne se totalise pas, plutôt
 * que d'additionner une grandeur qu'on n'a pas reconnue.
 */
const UNITES_ENERGIE = new Set([
    "wh",
    "kwh",
    "mwh",
    "gwh",
    "varh",
    "kvarh",
    "mvarh",
    "vah",
    "kvah",
    "mvah",
])

/** Vrai quand l'unité nomme une énergie (kWh, kvarh…), à la casse près. */
export function estUniteDEnergie(unite: string | null): boolean {
    if (!unite) return false
    return UNITES_ENERGIE.has(unite.trim().toLowerCase())
}

/**
 * Vrai quand CE graphe peut porter un total par série sur la fenêtre regardée.
 * Sans unité connue, non : une somme sans unité ne se lit pas.
 */
export function estSommableSurLaFenetre(
    onglet: string,
    unite: string | null
): boolean {
    if (!estSommable(onglet)) return false
    // LES DURÉES DE DÉPASSEMENT SE TOTALISENT AUSSI (Q25 du 17/09). Le brief B4
    // les laissait dehors parce qu'un total de secondes ne se lit pas comme une
    // quantité consommée ; Mathieu tranche l'autre sens. L'unité qui arrive ici
    // est déjà l'HEURE : les secondes servies sont converties au rangement de
    // la série (écart G11).
    if (onglet === "depassements" && estUniteDeDuree(unite)) return true
    return estUniteDEnergie(unite)
}

/**
 * LES JOURS RÉELLEMENT MESURÉS d'un chargement (DEV-1508 lot 2, E3).
 *
 * La bande de complétude se peint sur le contexte, jour par jour. Elle n'a pas
 * besoin d'une requête de plus : les séries que l'onglet vient de charger disent
 * déjà quels jours portent une mesure.
 *
 * Un jour est MESURÉ dès qu'un point fini y a été servi. Un `null` ne compte
 * pas, et un `0` compte : c'est une mesure à zéro, pas une absence (décision de
 * Mathieu du 07/09). Un point de période (`[début, fin]`) est daté par son
 * DÉBUT, comme partout ailleurs sur cette page.
 */
export function joursMesuresDesReponses(reponses: ReponseSeries[]): string[] {
    const jours = new Set<string>()
    for (const reponse of reponses) {
        for (const serie of reponse.data ?? []) {
            const x = serie.x ?? []
            const v = serie.v ?? []
            for (let i = 0; i < x.length; i += 1) {
                const valeur = v[i]
                if (typeof valeur !== "number" || !Number.isFinite(valeur))
                    continue
                const point = x[i]
                const brut = Array.isArray(point) ? point[0] : point
                if (typeof brut !== "string") continue
                jours.add(brut.slice(0, 10))
            }
        }
    }
    return [...jours].sort()
}

// ============================================================================
// LES JOURS MESURÉS DE LA BANDE (lot 5a, écart D2)
// ============================================================================

/** La variable de référence de la complétude, pour un sens. */
export function variableDeLaBande(direction: string): string {
    return `energieactive_elec${direction}_tout`
}

/**
 * LES JOURS RÉELLEMENT MESURÉS, POUR LA BANDE DE COMPLÉTUDE.
 *
 * Trois différences avec `joursMesuresDesReponses`, et chacune répare un mot
 * faux servi au client :
 *
 *  1. UNE SEULE GRANDEUR compte, l'énergie active journalière du sens. La bande
 *     dit « n / m jours mesurés » du compteur ; la faire dépendre des séries de
 *     l'onglet ouvert lui faisait dire deux choses différentes pour un même
 *     contexte (C2 réel du 15/09 : 360 / 365 sur Énergie, 363 / 365 sur
 *     Puissance). Une question, une réponse.
 *
 *  2. UN 0 EST UNE MESURE QUAND SON ORIGINE LE DIT. Un jour à zéro dont le point
 *     est servi `raw` est une mesure vraie — un bâtiment vide, une coupure
 *     connue — et il compte mesuré. Ce qui ne compte pas, ce n'est pas la
 *     VALEUR, c'est l'absence d'origine : un pas non collecté n'a aucune
 *     provenance servie (`t[i] = null`), et c'est cela, et cela seul, qui le
 *     distingue d'une consommation nulle (`origineEnedis.ts`, règle 3).
 *
 *     ⚠️ Écarter le 0 par sa valeur, comme ce code l'a fait un temps, aurait
 *     compté « Manquant » chaque journée réellement nulle. Une règle sur la
 *     valeur ne sait pas distinguer « rien consommé » de « rien reçu » ; seule
 *     l'origine le sait, et elle est servie avec chaque point.
 *
 *  3. UN JOUR INCOMPLET EST UN JOUR AVEC DONNÉE. Un point agrégé sur moins de
 *     pas que le jour n'en attend (`meta.partiel`, `meta.n`) porte l'origine
 *     ESTIMÉE — et il COMPTE dans la bande. Une absence, c'est un jour dont
 *     AUCUNE mesure n'est arrivée ; un jour incomplet en a reçu. Le peindre
 *     « Manquant » dirait au client qu'il n'a rien reçu ce jour-là, ce qui est
 *     faux, et c'est la même confusion que celle qu'on répare ailleurs.
 *
 *     L'incomplétude ne disparaît pas pour autant : elle se lit où elle a un
 *     sens, sur la pastille « Estimé » de la carte et dans le dépliant, qui
 *     tiennent tous deux la règle 2 d'`origineEnedis.ts`. La bande, elle,
 *     répond à une seule question : ce jour-là, une mesure est-elle arrivée ?
 *
 * Les TROIS règles se ramènent donc à une : compte le jour dont la valeur est
 * finie ET dont la provenance est SERVIE. Les quatre origines comptent ;
 * l'absence d'origine, jamais.
 *
 * Rend `null` — et non `[]` — quand aucune série de référence n'est servie : la
 * bande n'a alors rien lu, ce qui ne veut pas dire qu'elle a lu zéro jour.
 */

export function joursMesuresDeLaBande(
    reponses: ReponseSeries[],
    variablesDeReference: string[]
): string[] | null {
    const references = new Set(variablesDeReference)
    const jours = new Set<string>()
    let vue = false
    for (const reponse of reponses) {
        for (const serie of reponse.data ?? []) {
            if (!references.has(serie.variable)) continue
            if (serie.stat !== "sum") continue
            vue = true
            const cle = `${serie.variable}.${serie.stat}`
            // L'origine se lit POINT PAR POINT, par la même fonction que les
            // graphes et le dépliant : une seule règle d'origine dans la page.
            const origines = origineDesPoints(
                serie.t,
                reponse.meta?.n?.[cle],
                reponse.meta?.partiel?.[cle],
                attenduParPoint(
                    (reponse.meta?.grain ?? "daily") as GrainSeries,
                    null
                )
            ).parPoint
            const x = serie.x ?? []
            const v = serie.v ?? []
            for (let i = 0; i < x.length; i += 1) {
                const valeur = v[i]
                if (typeof valeur !== "number" || !Number.isFinite(valeur))
                    continue
                // Une provenance servie, quelle qu'elle soit : mesure, calcul,
                // correction OU estimation. Seul un point sans origine — un pas
                // que personne n'a collecté — ne compte pas.
                if (!origines[i]) continue
                const point = x[i]
                const brut = Array.isArray(point) ? point[0] : point
                if (typeof brut !== "string") continue
                jours.add(brut.slice(0, 10))
            }
        }
    }
    return vue ? [...jours].sort() : null
}

// ============================================================================
// UNE CLASSE, UNE LIGNE (lot 5d4)
// ============================================================================
//
// LE DÉFAUT MESURÉ le 16/09 sur le C2 `6a8eebfa` : l'onglet Dépassements
// listait « heures de pointe » DEUX fois. Le compteur nomme sa pointe `P` dans
// ses index et ses durées de dépassement, `Pointe` dans ses périodes de
// facturation ; la page demande donc les deux noms — on ne devine pas lequel ce
// compteur valorise — et le lecteur n'en sert qu'un.
//
// La canonisation du code (`classeCanonique`) fait que les deux portent
// désormais le MÊME libellé client. Il reste à n'en peindre qu'une ligne.
//
// CE QU'ON NE FAIT PAS, ET POURQUOI : concaténer les points des deux séries.
// Elles couvrent en général la même fenêtre sous deux noms, mais pas toujours :
// une bascule de convention en cours d'historique (`P` jusqu'en mars, `Pointe`
// ensuite) donne deux séries PLEINES sur des dates disjointes. Garder la
// première et jeter la seconde perdrait la moitié de la courbe. On recolle donc
// POINT À POINT, par abscisse.
//
// ⚠️ DEUX VALEURS FINIES SUR LA MÊME ABSCISSE NE S'ADDITIONNENT JAMAIS. Ce
// serait doubler une durée que le compteur a servie une fois sous deux noms. La
// première l'emporte, et le nombre de ces collisions est compté dans
// `conflitsDeFusion` — un fait qui se lit, plutôt qu'un choix qui se cache.

/** Vrai quand aucun point de la série n'est un nombre fini. */
export function aucunPointFini(serie: SerieTracee): boolean {
    return !serie.v.some((v) => typeof v === "number" && Number.isFinite(v))
}

/**
 * LA STATISTIQUE N'EST PAS SERVIE À CE GRAIN (lot 5d4, addendum 2).
 *
 * DÉFAUT MESURÉ le 16/09 sur le C4 réel `638f1080`, onglet Puissance au niveau
 * horaire : la carte « Puissance réactive totale » dessinait sa moyenne et
 * listait dessous « (minimum) · Aucune mesure sur cette fenêtre » et
 * « (maximum) · … ». Ce compteur ne déclare la réactive qu'au grain `natif` ;
 * dapi en dérive la moyenne à l'heure, mais rend le minimum et le maximum en
 * abscisses TOUTES nulles — indistinguables d'une absence depuis `/series`.
 *
 * Accuser la fenêtre d'un vide qui vient du grain ferait chercher au lecteur
 * une courbe qu'aucun réglage de bornes ne ramènera. Le fait qui tranche est la
 * DÉCLARATION, lue de `variables_datav2` ; en son absence (`undefined`), on ne
 * filtre rien : une absence vraie ne disparaît jamais par précaution.
 */
export function serieNonServie(serie: SerieTracee): boolean {
    return !serie.nonPubliee && serie.grainDeclare === false
}

/** Un point d'une série, avec tout ce que le lecteur en a dit. */
type PointDeSerie = {
    x: PointX
    v: number | null
    t: ProvenanceSeries | null
    n: number | null
    partiel: boolean | null
    at: string | null
    nature: string | null
}

/** L'abscisse d'un point, en clé comparable. Une période vaut par son couple. */
function cleDeLAbscisse(x: PointX): string {
    return Array.isArray(x) ? x.join("|") : x
}

/** Les points d'une série, alignés. Un tableau non servi rend `null` partout. */
function pointsDeLaSerie(serie: SerieTracee): PointDeSerie[] {
    return serie.x.map((x, k) => ({
        x,
        v: serie.v[k] ?? null,
        t: serie.t[k] ?? null,
        n: serie.n?.[k] ?? null,
        partiel: serie.partiel?.[k] ?? null,
        at: serie.at?.[k] ?? null,
        nature: serie.natureParPoint?.[k] ?? null,
    }))
}

/** Vrai pour une valeur réellement mesurée. Un `null` ou un `NaN` n'en est pas. */
function estFinie(v: number | null): boolean {
    return typeof v === "number" && Number.isFinite(v)
}

/**
 * Deux séries qui nomment la même chose, recollées POINT À POINT.
 *
 * Union des abscisses, triée. Sur une abscisse que les deux portent, la valeur
 * FINIE l'emporte sur le vide, et la PREMIÈRE l'emporte sur la seconde quand
 * les deux sont finies — jamais une somme. Un tableau que ni l'une ni l'autre ne
 * sert reste `null` : « non dit » ne devient pas « zéro fois ».
 */
function recollerDeuxSeries(
    base: SerieTracee,
    autre: SerieTracee
): SerieTracee {
    const points = new Map<string, PointDeSerie>()
    for (const point of pointsDeLaSerie(base)) {
        points.set(cleDeLAbscisse(point.x), point)
    }
    let conflits = 0
    for (const point of pointsDeLaSerie(autre)) {
        const cle = cleDeLAbscisse(point.x)
        const deja = points.get(cle)
        if (!deja) {
            points.set(cle, point)
            continue
        }
        if (estFinie(deja.v) && estFinie(point.v)) {
            conflits += 1
            continue
        }
        if (!estFinie(deja.v) && estFinie(point.v)) points.set(cle, point)
    }
    const tries = [...points.entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([, point]) => point)
    const servi = (a: unknown[] | null, b: unknown[] | null) =>
        a !== null || b !== null
    return {
        ...base,
        x: tries.map((p) => p.x),
        v: tries.map((p) => p.v),
        t: tries.map((p) => p.t),
        n: servi(base.n, autre.n) ? tries.map((p) => p.n) : null,
        partiel: servi(base.partiel, autre.partiel)
            ? tries.map((p) => p.partiel)
            : null,
        at: servi(base.at, autre.at) ? tries.map((p) => p.at) : null,
        natureParPoint: servi(base.natureParPoint, autre.natureParPoint)
            ? tries.map((p) => p.nature)
            : null,
        conflitsDeFusion: (base.conflitsDeFusion ?? 0) + conflits,
    }
}

/**
 * Une seule ligne par classe : les séries qui partagent direction, statistique
 * et libellé client nomment la même chose sous deux codes servis.
 */
export function fusionnerClassesSynonymes(
    series: SerieTracee[]
): SerieTracee[] {
    const gardees = new Map<string, SerieTracee>()
    for (const serie of series) {
        const cle = `${serie.direction}|${serie.stat}|${serie.libelle}|${serie.unite ?? ""}`
        const deja = gardees.get(cle)
        gardees.set(cle, deja ? recollerDeuxSeries(deja, serie) : serie)
    }
    return [...gardees.values()]
}

// ============================================================================
// UNE CARTE SANS AUCUNE VALEUR SERVIE (lot 5d4)
// ============================================================================
//
// La règle de « aucune période close » se généralise : quand AUCUNE série de la
// carte ne porte un seul point fini sur tout ce qui a été chargé, la carte ne
// dessine rien du tout — ni figure, ni bande, ni badge par classe — et le dit
// en une phrase, avec la date de la dernière valeur que la famille détient.
//
// Pourquoi la fenêtre ne suffit pas à l'expliquer : « Aucune mesure sur cette
// fenêtre » affirme que c'est la fenêtre qui est vide, donc qu'en élargir les
// bornes ramènerait la courbe. Sur le C2 témoin, la durée de dépassement est
// figée depuis le 16/01/2026 : la fenêtre n'y est pour rien, et la date est le
// seul fait qui apprenne quelque chose au lecteur.

/** Les familles dont le nom est masculin. Toutes les autres sont féminines. */
const FAMILLES_MASCULINES = new Set(["Index"])

/**
 * La phrase d'une carte sans aucune valeur servie. La date n'est écrite que si
 * elle est servie : sans elle, la phrase s'arrête, elle n'invente pas un jour.
 */
export function phraseCarteSansValeur(
    libelleFamille: string | null | undefined,
    jour: string | null | undefined
): string {
    const famille = libelleFamille ?? ""
    const article = FAMILLES_MASCULINES.has(famille) ? "Aucun" : "Aucune"
    const servi = FAMILLES_MASCULINES.has(famille) ? "servi" : "servie"
    const nom = famille
        ? `${famille.charAt(0).toLowerCase()}${famille.slice(1)}`
        : "valeur"
    const phrase = `${article} ${nom} ${servi} sur cette fenêtre`
    return jour ? `${phrase} · dernière le ${jour}` : `${phrase}.`
}

/**
 * LES BORNES DE L'AXE DES ORDONNÉES, décidées ICI et une seule fois.
 *
 * DÉFAUT MESURÉ le 16/09 sur le C4 réel `638f1080` : l'onglet Dépassements sert
 * quatre-vingt-douze durées, toutes à 0 h. Les bornes valaient alors `[0, 0]` —
 * deux bornes identiques, que Plotly remplace par son propre cadrage : un axe
 * de −0,5 à 1,0, donc des durées de dépassement NÉGATIVES graduées à l'écran.
 *
 * Une grandeur non négative ne descend jamais sous zéro. Un minimum négatif
 * réellement servi (une injection) est conservé : c'est un fait, pas un artefact.
 */
export function bornesDeLAxeY(params: {
    finis: number[]
    /** L'axe d'un index se cadre sur les valeurs, pas sur zéro (7.13). */
    auxValeurs: boolean
}): [number, number] | null {
    const { finis, auxValeurs } = params
    if (!finis.length) return null
    const min = Math.min(...finis)
    const max = Math.max(...finis)
    if (!auxValeurs) {
        const bas = min < 0 ? min : 0
        // Tout à zéro : le haut ne peut pas venir des valeurs, il vient de
        // l'unité. Le bas, lui, reste zéro — jamais une valeur négative.
        return max > 0 ? [bas, max * 1.2] : [bas, bas === 0 ? 1 : 0]
    }
    const ecart = max - min
    const souffle = ecart > 0 ? ecart / 1000 : Math.abs(max) / 1000 || 1
    return [min - souffle, max + souffle]
}

/**
 * LA DERNIÈRE ABSCISSE QUI PORTE UNE VALEUR FINIE, dans ce qui est chargé.
 *
 * FAIT VÉRIFIÉ en prod le 16/09 sur le C4 `638f1080` : `variables_datav2` borne
 * la réactive et l'apparente au 15/09 au grain natif, et `/series` rend 576
 * abscisses TOUTES nulles sur la fenêtre du 09 au 15/09. La borne compte donc
 * des documents à valeur nulle — une absence connue — et non la dernière valeur
 * mesurée. Écrire « dernière valeur le 15/09/2026 » depuis cette borne
 * annoncerait une mesure que la page ne montre nulle part.
 *
 * Ce qui se lit ici est ce qui est VRAIMENT servi : la dernière date portant un
 * nombre fini. `null` quand il n'y en a aucune, et rien ne s'écrit alors.
 */
export type DernierPointFini = {
    /** Le début du point : une date simple l'est aussi. */
    debut: string
    /**
     * Le DERNIER JOUR COUVERT quand l'abscisse est une période, c'est-à-dire
     * la veille de la fin exclusive servie. `null` sur un point daté.
     */
    fin: string | null
}

/**
 * LE DERNIER POINT FINI, avec ses deux bornes quand il en a deux (lot 5d4b).
 *
 * Une période de facturation est datée par son DÉBUT sur toute cette page —
 * c'est sa place sur l'axe. Mais la dernière donnée qu'elle porte est celle de
 * sa FIN : écrire « dernière période close le 01/08/2026 » sur une période qui
 * court jusqu'au 31/08 annonce un mois de retard qui n'existe pas (recette du
 * 16/09 sur le C2 témoin). Le point est donc choisi sur la fin quand elle est
 * servie, et rendu entier pour que l'appelant écrive ce qu'il sait.
 */
export function dernierPointFini(
    series: SerieTracee[]
): DernierPointFini | null {
    let dernier: DernierPointFini | null = null
    for (const serie of series) {
        serie.x.forEach((x, k) => {
            if (!estFinie(serie.v[k] ?? null)) return
            // La fin servie est EXCLUSIVE : le dernier jour couvert est la
            // veille (lot 5d4c). Sans ce recul, le C2 témoin annonçait
            // « dernière période close le 01/09/2026 » sur une période qui
            // s'arrête le 31/08.
            const point: DernierPointFini = Array.isArray(x)
                ? { debut: x[0].slice(0, 10), fin: finInclusiveDeLaPeriode(x) }
                : { debut: x.slice(0, 10), fin: null }
            const cle = point.fin ?? point.debut
            if (!dernier || cle > (dernier.fin ?? dernier.debut)) {
                dernier = point
            }
        })
    }
    return dernier
}

/**
 * LA DERNIÈRE DATE QUI PORTE UNE VALEUR FINIE, dans ce qui est chargé.
 *
 * FAIT VÉRIFIÉ en prod le 16/09 sur le C4 `638f1080` : `variables_datav2` borne
 * la réactive et l'apparente au 15/09 au grain natif, et `/series` rend 576
 * abscisses TOUTES nulles sur la fenêtre du 09 au 15/09. La borne compte donc
 * des documents à valeur nulle — une absence connue — et non la dernière valeur
 * mesurée. Écrire « dernière valeur le 15/09/2026 » depuis cette borne
 * annoncerait une mesure que la page ne montre nulle part.
 */
export function derniereAbscisseFinie(series: SerieTracee[]): string | null {
    const dernier = dernierPointFini(series)
    return dernier ? (dernier.fin ?? dernier.debut) : null
}

// ============================================================================
// LA FIN D'UNE PÉRIODE SERVIE EST EXCLUSIVE (lot 5d4c)
// ============================================================================
//
// CE QUI L'ÉTABLIT, et non ce qui le suggère :
//
//  • sur le C4 `638f1080` en prod le 16/09, `variables_datav2` borne
//    `energieactive_elecsoutirage_turpe*` au grain `periode` à
//    `date_to = 2026-08-15`, tandis que `/series` rend la dernière période
//    `["2026-07-16", "2026-08-16"]`. La borne est INCLUSIVE, le `x[1]` servi
//    vaut un jour de plus : c'est le témoin ;
//  • le tableau « Valeurs par période » du même compteur montrait « Du
//    16/07/2026 · Au 16/08/2026 » puis « Du 15/06/2026 · Au 16/07/2026 » :
//    deux périodes consécutives partageaient le 16/07. Un jour ne se facture
//    pas deux fois ;
//  • le témoin de récit `PERIODES_C5_REELLES`, relevé le 16/09 sur `6931c727`,
//    porte la même forme : `…, "2025-11-15"], ["2025-11-15", …`.
//
// `x[1]` est donc le DÉBUT DE LA PÉRIODE SUIVANTE. La dernière journée
// réellement couverte est la veille, et c'est elle qui s'écrit — dans la ligne
// de fraîcheur comme dans la colonne « Au » du tableau, par CE seul utilitaire.

/** Un jour civil ISO, reculé d'une journée. `null` sur ce qui n'est pas un jour. */
export function veilleDe(jour: string | null | undefined): string | null {
    const iso = String(jour ?? "").slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null
    const d = new Date(`${iso}T00:00:00Z`)
    if (Number.isNaN(d.getTime())) return null
    d.setUTCDate(d.getUTCDate() - 1)
    return d.toISOString().slice(0, 10)
}

/**
 * LE DERNIER JOUR COUVERT par une période servie, ou `null` hors période.
 *
 * Ce qu'on ne fait pas : reculer d'un jour une date qui n'en est pas une, ni
 * une période dégénérée dont la fin précède ou égale le début — elle rendrait
 * un « Au » antérieur au « Du ».
 */
export function finInclusiveDeLaPeriode(x: PointX): string | null {
    if (!Array.isArray(x)) return null
    const debut = String(x[0] ?? "").slice(0, 10)
    const veille = veilleDe(x[1])
    return veille && veille >= debut ? veille : null
}

// ============================================================================
// L'EMPRISE EN X D'UNE BARRE (DEV-1508 lot 5h, décision Q24 du 17/09)
// ============================================================================
//
// « les graphiques qui affichent des données sur des périodes doivent être des
// barres avec l'emprise en x de la période. »
//
// LE DÉFAUT QUE CETTE RÈGLE FERME : posée sans largeur, une barre Plotly est
// CENTRÉE sur son abscisse et dimensionnée sur le plus petit écart de la série.
// Un mois d'août se lisait alors comme un relevé posé à cheval sur la fin
// juillet, et douze mois de longueurs différentes portaient tous la largeur du
// mois le plus court. Une quantité qui couvre une période doit occuper CETTE
// période, du premier jour au dernier, et rien d'autre.
//
// La durée ne se devine pas : elle se lit de la période servie quand elle est
// servie (`[début, fin]`, fin EXCLUSIVE — lot 5d4c), sinon du GRAIN de la
// carte, dont le calendrier donne la durée exacte (un mois de 28, 30 ou 31
// jours reste un mois). Au grain `natif`, le pas dépend du compteur et n'est
// pas connu ici : aucune emprise n'est posée, et Plotly décide comme avant.

/** Un jour civil en millisecondes. */
export const JOUR_MS = 86_400_000
/** Une heure en millisecondes. */
export const HEURE_MS = 3_600_000

/** Les grains dont le calendrier donne une durée exacte, sans le lecteur. */
const DUREE_FIXE_DU_GRAIN: Record<string, number> = {
    hourly: HEURE_MS,
    daily: JOUR_MS,
    weekly: 7 * JOUR_MS,
}

/** Le nombre de jours du mois civil qui commence à `jour`, ou `null`. */
function joursDuMois(jour: string): number | null {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(jour)) return null
    const debut = new Date(`${jour.slice(0, 8)}01T00:00:00Z`)
    if (Number.isNaN(debut.getTime())) return null
    const suivant = new Date(debut)
    suivant.setUTCMonth(suivant.getUTCMonth() + 1)
    return Math.round((suivant.getTime() - debut.getTime()) / JOUR_MS)
}

/**
 * L'EMPRISE D'UN POINT, en millisecondes, ou `null` quand elle n'est pas
 * connue. C'est la largeur que la barre doit occuper, et son point de départ
 * est l'abscisse elle-même.
 *
 * Une période servie prime toujours sur le grain : c'est le lecteur qui sait
 * jusqu'où elle court. Une période dégénérée (fin ≤ début) n'en est pas une, et
 * ne fabrique pas une largeur négative.
 */
export function empriseDuPoint(
    x: PointX,
    grain: string | null | undefined,
    /**
     * LE CONTEXTE CHARGÉ, QUI BORNE LA BARRE (lot 5g, annexe de la revue du
     * lot 5h).
     *
     * Le lecteur date un mois par son PREMIER jour, même quand le contexte n'en
     * couvre qu'une partie : un contexte ouvert le 15/01 rendait une barre de
     * trente-et-un jours posée au 01/01, qui débordait de seize jours à gauche
     * de tout ce qui a été chargé — et symétriquement à droite sur le mois en
     * cours. Le client lisait une quantité étalée sur des jours dont la page
     * n'a rien demandé.
     *
     * Bornes INCLUSIVES, comme partout sur cette page : le dernier jour du
     * contexte appartient au contexte. Omis, rien n'est borné et l'emprise est
     * celle du calendrier, exactement comme avant.
     */
    contexte?: { debut: string; fin: string } | null
): number | null {
    const bornes = bornesDuPoint(x, grain, contexte)
    return bornes ? bornes.fin - bornes.debut : null
}

/**
 * LES DEUX BORNES D'UN POINT, en millisecondes : le début réel de la barre et
 * sa fin exclusive, toutes deux ramenées dans le contexte chargé.
 *
 * `null` quand l'emprise n'est pas connue, et quand le point tombe ENTIÈREMENT
 * hors du contexte : une barre de largeur nulle ou négative ne se dessine pas.
 */
export function bornesDuPoint(
    x: PointX,
    grain: string | null | undefined,
    contexte?: { debut: string; fin: string } | null
): { debut: number; fin: number } | null {
    let debut: number
    let fin: number
    if (Array.isArray(x)) {
        debut = Date.parse(`${String(x[0]).slice(0, 10)}T00:00:00Z`)
        fin = Date.parse(`${String(x[1]).slice(0, 10)}T00:00:00Z`)
        if (!Number.isFinite(debut) || !Number.isFinite(fin)) return null
        if (fin <= debut) return null
    } else {
        const xMs = Date.parse(`${String(x).slice(0, 10)}T00:00:00Z`)
        const duree =
            grain === "monthly"
                ? (joursDuMois(String(x).slice(0, 10)) ?? 0) * JOUR_MS || null
                : (DUREE_FIXE_DU_GRAIN[grain ?? ""] ?? null)
        if (duree === null) return null
        if (!Number.isFinite(xMs)) return null
        // AU GRAIN `daily` OU `hourly`, LE POINT EST SA PROPRE UNITÉ, PAS LE
        // DÉBUT D'UNE PÉRIODE PLUS LONGUE (défaut relevé le 17/09, lot 5h
        // annexe) : une barre journalière est CENTRÉE sur son jour, de J−12 h
        // à J+12 h (B1bis), exactement comme la case que la bande de fond
        // couvre pour le même jour absent. `bornesDuPoint`, posé sans cette
        // exception, ancrait la barre à SON DÉBUT comme pour un mois ou une
        // semaine — un jour se lisait alors décalé d'une demi-journée à
        // droite, et la hachure du jour suivant mordait sur sa moitié gauche.
        // `weekly`/`monthly`/`periode`, eux, restent ancrés à `x`, qui EST le
        // premier jour de leur période (test-vérifié, contexte tronqué).
        if (grain === "daily" || grain === "hourly") {
            debut = xMs - duree / 2
            fin = xMs + duree / 2
        } else {
            debut = xMs
            fin = xMs + duree
        }
    }
    if (!contexte) return { debut, fin }
    const borneBasse = Date.parse(`${contexte.debut.slice(0, 10)}T00:00:00Z`)
    // La fin du contexte est INCLUSIVE : le lendemain de son dernier jour est
    // la première milliseconde qui ne lui appartient plus.
    const borneHaute =
        Date.parse(`${contexte.fin.slice(0, 10)}T00:00:00Z`) + JOUR_MS
    if (!Number.isFinite(borneBasse) || !Number.isFinite(borneHaute)) {
        return { debut, fin }
    }
    const borne = {
        debut: Math.max(debut, borneBasse),
        fin: Math.min(fin, borneHaute),
    }
    return borne.fin > borne.debut ? borne : null
}

/**
 * LE DERNIER JOUR COUVERT par un point, pour le survol : « du 01/08/2026 au
 * 31/08/2026 ». Le lendemain de la fin n'appartient pas à la période, et deux
 * périodes voisines ne se partagent jamais un jour.
 *
 * `null` quand l'emprise n'est pas connue, et quand elle ne couvre qu'un seul
 * jour ou moins : « du 12/08 au 12/08 » n'apprend rien que l'abscisse ne dise.
 */
export function finCouverteDuPoint(
    x: PointX,
    grain: string | null | undefined
): string | null {
    if (Array.isArray(x)) return finInclusiveDeLaPeriode(x)
    const emprise = empriseDuPoint(x, grain)
    if (emprise === null || emprise <= JOUR_MS) return null
    const debut = Date.parse(`${String(x).slice(0, 10)}T00:00:00Z`)
    if (!Number.isFinite(debut)) return null
    return new Date(debut + emprise - JOUR_MS).toISOString().slice(0, 10)
}

// ============================================================================
// LE TOTAL DES DURÉES DE DÉPASSEMENT (DEV-1508 lot 5h, décision Q25 du 17/09)
// ============================================================================
//
// « oui, comme une stat » : sur l'onglet Dépassements, le bloc des totaux porte
// une statistique PAR CLASSE TEMPORELLE et un total, dans l'unité que le client
// lit — des heures.
//
// LA CONVERSION N'A PAS LIEU ICI, et c'est voulu : les secondes servies par
// dataV2 deviennent des heures UNE SEULE FOIS, au rangement de la série (écart
// G11, `estDureeEnSecondes` / `heuresDepuisSecondes`). Le tracé, le survol, le
// tableau et ce total lisent donc tous la même valeur, dans la même unité.
// Reconvertir ici diviserait deux fois.
//
// DEUX RÈGLES QUI NE SE CONFONDENT PAS (art. « absence ≠ 0 ») :
//  • un 0 SERVI vaut 0 h. C'est une mesure : le compteur n'a pas dépassé ;
//  • une classe dont AUCUNE valeur n'est servie sur la fenêtre écrit « aucune
//    valeur ». Écrire 0 h ferait passer un trou de collecte pour un compteur
//    sage, et c'est la 18ᵉ forme de garde qui ment.

/** Vrai quand l'unité de la carte est celle d'une durée, en heures. */
export function estUniteDeDuree(unite: string | null | undefined): boolean {
    return (
        String(unite ?? "")
            .trim()
            .toLowerCase() === UNITE_HEURE
    )
}

/**
 * Une durée en heures, écrite pour le client : « 0 », « 2,8 », « 1 240 ».
 *
 * Une décimale quand elle change quelque chose, aucune sur un compte rond :
 * « 0,0 h » sur un compteur qui n'a jamais dépassé se lit comme un arrondi,
 * alors que c'est un zéro exact.
 */
export function heuresLisibles(heures: number): string {
    return new Intl.NumberFormat("fr-FR", {
        maximumFractionDigits: 1,
    }).format(heures)
}

/** Ce qu'écrit une classe dont aucune valeur n'est servie sur la fenêtre. */
export const AUCUNE_VALEUR_SERVIE = "aucune valeur"

/**
 * « 0 h sur 92 jours servis », « 0 h sur 88 jours servis, 4 partiels ».
 *
 * LE COMPTE DES JOURS DIT SUR QUOI PORTE LE TOTAL, et un jour ne compte comme
 * servi que si TOUTES les classes de la carte y ont une valeur (annexe de la
 * revue du lot 5h). Un jour où seules les heures pleines sont servies ne dit
 * rien des heures creuses : le compter plein ferait passer un total amputé pour
 * un total complet, exactement le reproche que « n jours servis » devait lever.
 *
 * Les jours PARTIELS ne disparaissent pas pour autant : leurs valeurs sont dans
 * le total, et les taire reviendrait à faire croire qu'ils n'existent pas.
 */
export function texteDuTotalDeLaFenetre(
    valeur: string,
    unite: string | null | undefined,
    joursServis: number,
    joursPartiels = 0
): string {
    const chiffre = unite ? `${valeur} ${unite}` : valeur
    const mot = joursServis === 1 ? "jour servi" : "jours servis"
    const base = `${chiffre} sur ${joursServis} ${mot}`
    if (joursPartiels <= 0) return base
    return `${base}, ${joursPartiels} ${joursPartiels === 1 ? "partiel" : "partiels"}`
}
